import React, { useEffect, useState } from 'react';
import { useNavigate } from "react-router-dom";
import {
  FluentProvider,
  teamsLightTheme,
  Input,
  Textarea,
  Button,
  Label,
  Spinner
} from '@fluentui/react-components';
import { fetchCodedCaseId } from "../services/CaseService";
import { fetchEmployeeDetails } from "../services/EmployeeService";
import * as microsoftTeams from "@microsoft/teams-js";

const PatientDetailsForm = () => {
  const navigate = useNavigate();
  const [codedCaseId, setCodedCaseId] = useState<string | null>(null);
  const [employeeDetails, setEmployeeDetails] = useState<any>(null);
  const [referenceCode, setReferenceCode] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        microsoftTeams.initialize();
        microsoftTeams.getContext(async (context) => {
          const teamsMeetingId = (context as any)?.chatId || ""; // Use chatId from teamsContext
          const data = await fetchCodedCaseId(teamsMeetingId);
          setCodedCaseId(data);
          const response = await fetchEmployeeDetails(data);
          setEmployeeDetails(response.details);
          setReferenceCode(response.referenceCode);
          setLoading(false);
        });
      } catch (error) {
        console.error("Error fetching coded case ID:", error);
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  if (loading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '90vh' }}>
        <Spinner size="large" label="Loading employee details..." />
      </div>
    );
  }

  return (
    <FluentProvider theme={teamsLightTheme}>
      <div style={{
        minHeight: '90vh',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'space-between',
        padding: 20,
        maxWidth: 500,
        margin: '0 auto',
        background: '#fff',
        boxSizing: 'border-box'
      }}>
        <div>
          <Button appearance="subtle" icon={<span style={{display:'inline-block',transform:'rotate(180deg)'}}>&#8594;</span>} onClick={() => navigate(-1)} aria-label="Back" />
        </div>
        <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'flex-start', gap: '1.5rem', marginTop: 10 }}>
          <div>
            <div style={{ fontWeight: 'bold' }}>Health Partners Note Assistant</div>
            <div style={{ fontWeight: 600, fontSize: '1.1rem' }}>{referenceCode || "Loading..."}</div>
            <div style={{ fontWeight: 600, fontSize: '1rem' }}>{employeeDetails?.forenames || "Loading..."} {employeeDetails?.surname || ""}</div>
          </div>

          <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
            <div>
              <Label htmlFor="first-name">First Name</Label>
              <Input id="first-name" value={employeeDetails?.forenames || ""} style={{ width: '100%' }} readOnly />
            </div>
            <div>
              <Label htmlFor="surname">Surname</Label>
              <Input id="surname" value={employeeDetails?.surname || ""} style={{ width: '100%' }} readOnly />
            </div>
            <div>
              <Label htmlFor="dob">Date Of Birth</Label>
              <Input 
                id="dob" 
                value={employeeDetails?.dateOfBirth ? 
                  new Date(employeeDetails.dateOfBirth).toLocaleDateString('en-GB', {
                    day: '2-digit',
                    month: '2-digit',
                    year: 'numeric'
                  }) : ""
                } 
                style={{ width: '100%' }} 
                readOnly 
              />
            </div>
            <div>
              <Label htmlFor="address">Address</Label>
              <Textarea
                id="address"
                value={`${employeeDetails?.line1 || ""}\n${employeeDetails?.line2 || ""}\n${employeeDetails?.postcode || ""}`}
                resize="none"
                style={{ width: '100%' }}
                readOnly
              />
            </div>
            <div>
              <Label htmlFor="email">Email Address</Label>
              <Input id="email" value={employeeDetails?.emailAddress || ""} style={{ width: '100%' }} readOnly />
            </div>
          </div>
        </div>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 24 }}>
          <Button 
            appearance="secondary" 
            onClick={() => navigate("/incorrect-details")}
          >
            Incorrect Details
          </Button>
          <Button appearance="primary" onClick={() => navigate("/consentScreen", { state: { referenceCode } })}>Confirm Details</Button>
        </div>
      </div>
    </FluentProvider>
  );
};

export default PatientDetailsForm;
